import { apiClient } from '@/lib/api-client';
import type { Report } from '../reports/types';
import type {
  HistoryItem,
  Month,
  MonthStatus,
  NewPayment,
  Payment,
  Problem,
  WarningCode,
} from './types';

export type Answer<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly problem: Problem };

type Raw = Promise<{ data?: unknown; error?: unknown; response: Response }>;

function problemOf(status: number, error: unknown): Problem {
  const body =
    typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : {};
  return {
    status,
    code: typeof body['code'] === 'string' ? body['code'] : '',
    ...(typeof body['remainingCents'] === 'number'
      ? { remainingCents: body['remainingCents'] }
      : {}),
    ...(Array.isArray(body['blockers'])
      ? { blockers: body['blockers'] as Problem['blockers'] }
      : {}),
  } as Problem;
}

async function call<T>(run: () => Raw): Promise<Answer<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok) return { ok: true, value: data as T };
    return { ok: false, problem: problemOf(response.status, error) };
  } catch {
    return { ok: false, problem: { status: 0, code: '' } };
  }
}

const path = (month: string) => ({ params: { path: { month } } });

export const listMonths = () =>
  call<{ items: MonthStatus[] }>(() => apiClient.GET('/v1/expenses/closings'));

export const getMonth = (month: string) =>
  call<Month>(() => apiClient.GET('/v1/expenses/closings/{month}', path(month)));

export const closeMonth = (month: string, confirmedWarnings: readonly WarningCode[]) =>
  call<Month>(() =>
    apiClient.POST('/v1/expenses/closings/{month}/close', {
      ...path(month),
      body: { confirmedWarnings: [...confirmedWarnings] },
    }),
  );

export const reopenMonth = (month: string, reason: string) =>
  call<Month>(() =>
    apiClient.POST('/v1/expenses/closings/{month}/reopen', {
      ...path(month),
      body: { reason: reason.trim() },
    }),
  );

export const listPayments = (reportId: string) =>
  call<{ items: Payment[] }>(() =>
    apiClient.GET('/v1/expenses/reports/{reportId}/payments', {
      params: { path: { reportId } },
    }),
  );

export const recordPayment = (reportId: string, body: NewPayment) =>
  call<{ payment: Payment; report: Report }>(() =>
    apiClient.POST('/v1/expenses/reports/{reportId}/payments', {
      params: { path: { reportId } },
      body,
    }),
  );

export const getHistory = (reportId: string) =>
  call<{ items: HistoryItem[] }>(() =>
    apiClient.GET('/v1/expenses/reports/{reportId}/history', {
      params: { path: { reportId } },
    }),
  );

/** The two downloads of a month: the CSV and the documents ZIP (the same route the API serves). */
export type DownloadKind = 'list' | 'documents';

const DOWNLOAD = {
  list: { route: '/v1/expenses/closings/{month}/export', parseAs: 'blob', ext: 'csv' },
  documents: {
    route: '/v1/expenses/closings/{month}/export/documents',
    parseAs: 'blob',
    ext: 'zip',
  },
} as const;

/**
 * Fetches a month's file through the typed client (so the bearer token and the X-Active-* headers
 * go with it) and hands the browser a blob to save, the way the prima nota's export does.
 */
export async function downloadMonth(month: string, kind: DownloadKind): Promise<boolean> {
  const spec = DOWNLOAD[kind];
  const answer = await call<Blob>(() =>
    apiClient.GET(spec.route, { ...path(month), parseAs: spec.parseAs }),
  );
  if (!answer.ok) return false;
  const url = URL.createObjectURL(answer.value);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `note-spese-${month}${kind === 'documents' ? '-documenti' : ''}.${spec.ext}`;
  anchor.click();
  URL.revokeObjectURL(url);
  return true;
}
